import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const languages = [
  { code: "tr", name: "Türkçe", flag: "🇹🇷" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "ku", name: "Kurdî", flag: "🟢" },
  { code: "ar", name: "العربية", flag: "🇸🇦" }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const { language, setLanguage, t } = useLanguage();

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLang = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: t("nav.services"), href: "#services" },
    { name: t("nav.venue"), href: "#venue" },
    { name: t("nav.about"), href: "#about" },
    { name: t("nav.gallery"), href: "#gallery" },
    { name: t("nav.contact"), href: "#contact" },
  ];

  const whatsappNumber = "+905488484828";
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/\+/g, "")}`;

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "nav-blur border-b border-white/10" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center space-x-2" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_web-yayini/artifacts/3lnxk1de_C4ED1F5A-9382-457C-A949-2A0F4A5E715E.png" 
                alt="First Lady VIP Services Logo" 
                className="h-14 md:h-16 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-sm font-medium tracking-[0.2em] text-gray-300 hover:text-[#FF00FF] transition-colors duration-300"
                  data-testid={`nav-link-${link.href.replace("#", "")}`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Right Side: Language + CTA */}
            <div className="hidden md:flex items-center gap-4">
              {/* Language Dropdown */}
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-[#D4AF37]/50 transition-all group"
                  data-testid="language-switcher"
                  aria-label="Change language"
                >
                  <Globe size={16} className="text-[#D4AF37]" />
                  <span className="text-sm font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                    {currentLang.flag} {currentLang.code.toUpperCase()}
                  </span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 bg-[#0A0A0A] border border-white/10 shadow-xl z-50 min-w-[140px]"
                      data-testid="language-dropdown"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsLangMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                            language === lang.code 
                              ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                              : 'text-white hover:bg-white/5 hover:text-[#D4AF37]'
                          }`}
                          data-testid={`lang-option-${lang.code}`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* CTA Button */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF00FF] text-black px-6 py-3 font-bold text-sm tracking-wider uppercase hover:scale-105 transition-transform neon-glow btn-clip"
                data-testid="navbar-cta"
              >
                <Phone size={16} />
                {t("nav.reservation")}
              </a>
            </div>

            {/* Mobile: Language + Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              {/* Mobile Language Dropdown */}
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1 px-2 py-1 border border-white/20"
                  data-testid="mobile-language-switcher"
                >
                  <span className="text-sm">{currentLang.flag}</span>
                  <ChevronDown size={12} className={`text-gray-400 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 bg-[#0A0A0A] border border-white/10 shadow-xl z-50 min-w-[120px]"
                      data-testid="mobile-language-dropdown"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsLangMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                            language === lang.code 
                              ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                              : 'text-white hover:bg-white/5'
                          }`}
                          data-testid={`mobile-lang-option-${lang.code}`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white p-2"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#050505] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-['Syne'] text-4xl font-bold tracking-tight text-white hover:text-[#FF00FF] transition-colors"
                  data-testid={`mobile-nav-link-${link.href.replace("#", "")}`}
                >
                  {link.name}
                </motion.a>
              ))}
              <motion.a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8 inline-flex items-center gap-2 bg-[#FF00FF] text-black px-8 py-4 font-bold text-lg tracking-wider uppercase neon-glow btn-clip"
                data-testid="mobile-cta"
              >
                <Phone size={20} />
                {t("nav.reservation")}
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
